import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, NumericInput, RadioGroup, TextOutput } from '../../Core';
import { FieldSet, ListFieldSection } from '../../Layout';
import { actions } from '../../../Data/ReduxViewModels/Equipment';
import deepEquals from '../../../Lib/Object/deepEquals';
import * as oPath from '../../../Lib/Object/Path/Immutable';
import { withInjectedProps } from '../../Core/InjectedPropsContext';
import {
  getSieveSetSizeOptionValueForDimensions,
  getSieveSetSizeOptions,
  SIZE_OPTION_CUSTOM_CIRCULAR,
  SIZE_OPTION_CUSTOM_RECTANGULAR,
  getSieveSetUnitsOptions,
} from './SieveSetHelper';

let SieveSetDimensionsSection = ({ namePrefix }) => {
  const dispatch = useDispatch();

  const units = useSelector((state) => state.equipment.syncModel.data.Units);
  const sieveSetDimensions = useSelector(
    (state) => oPath.get(state.equipment.syncModel, `${namePrefix}.Dimensions`),
    deepEquals
  );

  const sieveSetDimensionsOptions = getSieveSetSizeOptions(units);

  const matchingSieveSizeOption = getSieveSetSizeOptionValueForDimensions(sieveSetDimensions, units);

  const [sieveSetSizeOption, setSieveSetSizeOption] = useState(matchingSieveSizeOption);

  const changeSieveSetSizeOption = useCallback(
    (sieveSetSizeOption) => {
      let selectedOptionDimensions = sieveSetDimensionsOptions.find((o) => o.value === sieveSetSizeOption)?.dimensions;
      if (!selectedOptionDimensions) {
        throw new Error(`Could not obtain a default size for sieve size set option: ${sieveSetSizeOption}`);
      }

      dispatch(
        actions.setValue({
          name: `${namePrefix}.Dimensions`,
          value: selectedOptionDimensions,
          sync: true,
        })
      );

      setSieveSetSizeOption(sieveSetSizeOption);
    },
    [dispatch, namePrefix, sieveSetDimensionsOptions]
  );

  const changeUnits = useCallback(
    (units) => {
      if (!getSieveSetSizeOptions(units).some((o) => o.value === sieveSetSizeOption)) {
        setSieveSetSizeOption(null);
        dispatch(
          actions.setValue({
            name: `${namePrefix}.Dimensions`,
            value: null,
            sync: false,
          })
        );
      }
      dispatch(
        actions.setValue({
          name: `${namePrefix}.Units`,
          value: units,
          sync: true,
        })
      );
    },
    [dispatch, namePrefix, sieveSetSizeOption]
  );

  return (
    <ListFieldSection>
      <Field input={<TextOutput />} label="Sieve Set" name="Code" />
      <Field
        input={<RadioGroup />}
        label="Sieve Set Size Units"
        name="Units"
        options={getSieveSetUnitsOptions()}
        onChange={(e) => changeUnits(e.data.value)}
      />
      <FieldSet>
        <Field
          input={<RadioGroup />}
          label="Sieve Set Size"
          options={getSieveSetSizeOptions(units)}
          value={sieveSetSizeOption}
          onChange={(e) => changeSieveSetSizeOption(e.data.value)}
        />
        <Field
          input={<NumericInput />}
          label="Sieve Width"
          name="Dimensions.Width"
          visible={sieveSetSizeOption === SIZE_OPTION_CUSTOM_RECTANGULAR}
        />
        <Field
          input={<NumericInput />}
          label="Sieve Length"
          name="Dimensions.Length"
          visible={sieveSetSizeOption === SIZE_OPTION_CUSTOM_RECTANGULAR}
        />
        <Field
          input={<NumericInput />}
          label="Sieve Diameter"
          name="Dimensions.Diameter"
          visible={sieveSetSizeOption === SIZE_OPTION_CUSTOM_CIRCULAR}
        />
      </FieldSet>
    </ListFieldSection>
  );
};

SieveSetDimensionsSection = withInjectedProps(SieveSetDimensionsSection);

export default SieveSetDimensionsSection;
export { SieveSetDimensionsSection };
